import { useEffect } from "react";

/**
 * Keeps the screen awake while `isActive` is true (prevents mobile auto-lock,
 * which suspends the microphone). The browser releases the lock whenever the
 * page is hidden, so it is re-acquired each time the page becomes visible.
 */
export const useScreenWakeLock = (isActive: boolean) => {
  useEffect(() => {
    if (!isActive || !("wakeLock" in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let isDisposed = false;

    const requestLock = async () => {
      if (isDisposed || document.visibilityState !== "visible" || (sentinel && !sentinel.released)) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (isDisposed) {
          void lock.release();
          return;
        }
        sentinel = lock;
      } catch (error) {
        console.warn("Screen wake lock unavailable:", error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void requestLock();
    };

    void requestLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isDisposed = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      void sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [isActive]);
};
